import React, { useState, useEffect } from "react";
import { getData, serverURL } from "../../../Services/Fetchnodeservices";

const WhyChooseUs = ({ isDark = false }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for different cards
  const cardColors = [
    { bg: "#fef3c7", accent: "#d97706", gradient: "linear-gradient(135deg, #fef3c7, #fde68a)" },
    { bg: "#dbeafe", accent: "#2563eb", gradient: "linear-gradient(135deg, #dbeafe, #93c5fd)" },
    { bg: "#d1fae5", accent: "#059669", gradient: "linear-gradient(135deg, #d1fae5, #6ee7b7)" },
    { bg: "#fce7f3", accent: "#db2777", gradient: "linear-gradient(135deg, #fce7f3, #f9a8d4)" },
    { bg: "#ede9fe", accent: "#7c3aed", gradient: "linear-gradient(135deg, #ede9fe, #c4b5fd)" },
    { bg: "#fef3c7", accent: "#d97706", gradient: "linear-gradient(135deg, #fef3c7, #fde68a)" },
    { bg: "#dbeafe", accent: "#2563eb", gradient: "linear-gradient(135deg, #dbeafe, #93c5fd)" },
    { bg: "#d1fae5", accent: "#059669", gradient: "linear-gradient(135deg, #d1fae5, #6ee7b7)" },
  ];

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getData("chooseus/fetch_all_banner");
      console.log("API Response:", response);

      if (response.status) {
        // Filter only active and visible items
        const activeFeatures = response.data.filter(
          item => item.status === 1 || item.status === "1"
        );
        console.log("Active Features:", activeFeatures);
        setFeatures(activeFeatures);
      } else {
        setError(response.message || "Failed to fetch features");
      }
    } catch (error) {
      console.error("Error fetching features:", error);
      setError("Failed to load features. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    if (imageName.startsWith('http')) return imageName;
    // Try both possible paths
    const paths = [
      `${serverURL}/public/uploadnew/upload/${imageName}`,
      `${serverURL}/uploads/${imageName}`,
      `${serverURL}/uploadnew/upload/${imageName}`
    ];
    return paths[0]; // Return first path as default
  };

  const styles = {
    container: {
      padding: "40px 20px",
      background: isDark ? "#0a0c10" : "#f8fafc",
      minHeight: "100vh",
    },
    wrapper: {
      maxWidth: "1200px",
      margin: "0 auto",
    },
    heading: {
      textAlign: "center",
      marginBottom: "40px",
      marginTop: "20px",
    },
    title: {
      fontSize: "42px",
      fontWeight: "800",
      color: isDark ? "#f1f5f9" : "#1e293b",
      marginBottom: "12px",
      lineHeight: "1.2",
    },
    subtitle: {
      fontSize: "17px",
      color: isDark ? "#94a3b8" : "#64748b",
      maxWidth: "650px",
      margin: "0 auto",
      lineHeight: "1.8",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      justifyContent: "center",
      gap: "24px",
      padding: "10px 0",
    },
    card: (color, isDark) => ({
      background: isDark ? "#141824" : "#ffffff",
      borderRadius: "24px",
      padding: "24px",
      position: "relative",
      overflow: "hidden",
      border: isDark ? "1px solid #1e2740" : "1px solid #ede9fe",
      boxShadow: isDark 
        ? "0 10px 30px rgba(0,0,0,0.2)" 
        : "0 10px 30px rgba(124,58,237,0.08)",
      transition: "all 0.35s ease",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }),
    imageWrapper: (color) => ({
      width: "100%",
      height: "200px",
      borderRadius: "18px",
      overflow: "hidden",
      marginBottom: "18px",
      background: color?.bg || "#ede9fe",
      position: "relative",
      zIndex: 2,
    }),
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.4s ease",
    },
    cardTitle: (color) => ({
      color: color?.accent || "#7c3aed",
      fontSize: "20px",
      fontWeight: "800",
      marginBottom: "10px",
      lineHeight: "1.3",
    }),
    cardDesc: (isDark) => ({
      color: isDark ? "#94a3b8" : "#64748b",
      fontSize: "14px",
      lineHeight: "1.7",
      flex: 1,
    }),
    cardFooter: {
      marginTop: "16px",
      paddingTop: "16px",
      borderTop: "1px solid rgba(0,0,0,0.06)",
    },
    buttonLink: (color) => ({
      display: "inline-block",
      padding: "8px 20px",
      background: color?.accent || "#7c3aed",
      color: "#fff",
      borderRadius: "12px",
      fontSize: "13px",
      fontWeight: "600",
      textDecoration: "none",
      transition: "all 0.3s ease",
    }),
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "400px",
      flexDirection: "column",
      gap: "16px",
    },
    spinner: {
      width: "50px",
      height: "50px",
      border: "3px solid #e2e8f0",
      borderTopColor: "#7c3aed",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    errorContainer: {
      textAlign: "center",
      padding: "60px 20px",
      color: isDark ? "#f1f5f9" : "#1e293b",
    },
    retryBtn: {
      marginTop: "16px",
      padding: "10px 24px",
      background: "#7c3aed",
      color: "#fff",
      border: "none",
      borderRadius: "12px",
      fontSize: "14px",
      cursor: "pointer",
    },
    noData: {
      textAlign: "center",
      padding: "60px 20px",
      color: isDark ? "#94a3b8" : "#64748b",
      fontSize: "16px",
    },
    debugInfo: {
      marginTop: "20px",
      padding: "16px",
      background: isDark ? "#1e2740" : "#f1f5f9",
      borderRadius: "8px",
      fontSize: "12px",
      color: isDark ? "#94a3b8" : "#64748b",
      overflow: "auto",
      maxHeight: "200px",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
            Loading features...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <h2 style={{ marginBottom: "8px" }}>Something went wrong</h2>
          <p style={{ color: isDark ? "#94a3b8" : "#64748b" }}>{error}</p>
          <button style={styles.retryBtn} onClick={fetchFeatures}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Debug: Log features to console
  console.log("Features to render:", features);

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        {/* HEADING */}
        <div style={styles.heading}>
          <h2 style={styles.title}>Why Choose Us?</h2>
          <p style={styles.subtitle}>
            Experience the best delivery service with amazing features
          </p>
        </div>

        {/* FEATURES GRID */}
        {features && features.length > 0 ? (
          <div style={styles.grid}>
            {features.map((feature, index) => {
              const color = cardColors[index % cardColors.length];
              const imageUrl = getImageUrl(feature.pic);
              const iconUrl = getImageUrl(feature.shortimage);
              
              return (
                <article
                  key={feature.id || index}
                  style={styles.card(color, isDark)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-10px) scale(1.02)";
                    e.currentTarget.style.boxShadow = isDark
                      ? "0 20px 40px rgba(0,0,0,0.3)"
                      : "0 20px 40px rgba(124,58,237,0.18)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0px) scale(1)";
                    e.currentTarget.style.boxShadow = isDark
                      ? "0 10px 30px rgba(0,0,0,0.2)"
                      : "0 10px 30px rgba(124,58,237,0.08)";
                  }}
                >
                  {/* IMAGE - Show main image if available, else show icon */}
                  {(imageUrl || iconUrl) && (
                    <div style={styles.imageWrapper(color)}>
                      <img
                        src={imageUrl || iconUrl}
                        alt={feature.title || "Feature"}
                        style={styles.image}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `
                            <div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:48px;background:${color?.bg || '#ede9fe'}">
                              ✨
                            </div>
                          `;
                        }}
                      />
                    </div>
                  )}

                  {/* CONTENT */}
                  <div style={{ position: "relative", zIndex: 2, flex: 1 }}>
                    <h3 style={styles.cardTitle(color)}>
                      {feature.title || "Feature Title"}
                    </h3>

                    {feature.title2 && (
                      <p style={{ 
                        color: isDark ? "#94a3b8" : "#64748b", 
                        fontSize: "13px", 
                        fontWeight: "500",
                        marginBottom: "8px"
                      }}>
                        {feature.title2}
                      </p>
                    )}

                    <p style={styles.cardDesc(isDark)}>
                      {feature.description || "No description available"}
                    </p>

                    {/* BUTTON (if exists) */}
                    {feature.button_name && feature.button_name.trim() !== "" && (
                      <div style={styles.cardFooter}>
                        <a
                          href={feature.button_link || "#"}
                          style={styles.buttonLink(color)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          onClick={(e) => {
                            if (!feature.button_link) {
                              e.preventDefault();
                            }
                          }}
                        >
                          {feature.button_name}
                        </a>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div style={styles.noData}>
            <p>No features available at the moment. Please check back later.</p>
          </div>
        )}

    

      </div>
    </div>
  );
};

export default WhyChooseUs;